import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChangePasswordModal from '../components/ChangePasswordModal';

export default function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const { login, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || '/';

    useEffect(() => {
        console.log("LOGIN PAGE LOADED - VERSION 70/30 IMAGE");
        if (isAuthenticated && user && !user.must_change_password) {
            navigate(from, { replace: true });
        }

    }, [isAuthenticated, user, navigate, from]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(username, password);

        if (result.success) {
            if (result.user.must_change_password) {
                setShowPasswordModal(true);
            } else {
                navigate(from, { replace: true });
            }
        } else {
            setError(result.error);
        }
        setLoading(false);
    };

    const handlePasswordChanged = () => {
        setShowPasswordModal(false);
        navigate(from, { replace: true });
    };

    return (
        <div className="flex min-h-screen w-full bg-white font-sans overflow-hidden">

            {/* Left Side (70%) - Hero Image */}
            <div className="hidden md:block md:w-[70%] bg-gray-100 relative">
                <img
                    src="/Logo_gestao_leitos_cinza (1).png"
                    alt="Gestão de Leitos Hospitalar"
                    className="absolute inset-0 w-full h-full object-cover"
                />
            </div>

            {/* Right Side (30%) - Login Form */}
            <div className="w-full md:w-[30%] flex flex-col justify-center px-8 lg:px-12 py-12 shadow-2xl relative z-10 bg-white">
                <div className="w-full max-w-sm mx-auto space-y-8">

                    {/* Mobile Logo */}
                    <div className="md:hidden flex justify-center mb-8">
                        <img
                            src="/Logo_gestao_leitos_branco.png"
                            alt="LeitoFlow"
                            className="h-12 w-auto bg-blue-600 p-2 rounded-lg"
                        />
                    </div>

                    <div className="text-center md:text-left">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                            Bem-vindo de volta
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Por favor, insira suas credenciais para entrar.
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                                <div className="flex">
                                    <div className="ml-3">
                                        <p className="text-sm text-red-700">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-5">
                            <div>
                                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                                    Usuário
                                </label>
                                <input
                                    id="username"
                                    name="username"
                                    type="text"
                                    autoComplete="username"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-3 px-3 border"
                                    placeholder="Ex: admin"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                    Senha
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-3 px-3 border"
                                        placeholder="••••••••"
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? (
                                            <Eye className="h-5 w-5" />
                                        ) : (
                                            <EyeOff className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                                    Lembrar-me
                                </label>
                            </div>

                            <div className="text-sm">
                                <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                                    Esqueceu a senha?
                                </a>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? 'Entrando...' : 'Entrar no Sistema'}
                            </button>
                        </div>
                    </form>

                    <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                        <p className="text-xs text-gray-400">
                            &copy; 2024 LeitoFlow.
                        </p>
                    </div>
                </div>
            </div>

            <ChangePasswordModal
                show={showPasswordModal}
                onClose={() => { }}
                onSuccess={handlePasswordChanged}
                required={true}
            />
        </div>
    );
}
